import React, { useRef, useEffect } from "react";
import { useSectionInView } from "../assets/lib/hooks";
import { useTheme } from "../context/theme-context";
import RadialGradient from "./RadialGradient";
import { motion, useScroll, useTransform } from "framer-motion";
import * as d3 from "d3";
import { nest } from 'd3-collection';
import { useAppDispatch, useTypedSelector } from '../stateStore';
import { Outlet, Link } from "react-router-dom";

const SkillsTree: React.FC = () => {
  const skillHierarchyStatus = useTypedSelector(state => state.SkillHierarchy.loading)
  const skillHierarchyData = useTypedSelector(state => state.SkillHierarchy.data)

  // let screenWidth = [600, 800, 1000, 1200, 1500, 1800]

  useEffect(() => {
    handleResize()
    window.addEventListener('resize', function (event) {
      handleResize()
    });
  }, [skillHierarchyStatus, skillHierarchyData]);

  function handleResize() {
    if (!skillHierarchyStatus) {
      draw(window.innerHeight, window.document.body.clientWidth)
    }
  }

  function draw(clientHeight: number, clientWidth: number) {
    clientHeight = clientHeight * 0.82
    d3.selectAll("#treeSkillsDiv").remove()

    const root: any = d3.hierarchy(skillHierarchyData);
    const dx = 15;
    const dy = clientWidth /4;

    const tree = d3.tree().nodeSize([dx, dy]);
    const diagonal: any = d3.linkHorizontal().x((d: any) => d.y).y((d: any) => d.x);

    let svg = d3.select("#treeSkills")
      .append("div")
      .attr("id", "treeSkillsDiv")
      .append("svg")
      .attr("viewBox", "-100 -100 " + clientWidth + " " + clientHeight)
      .attr("style", "max-width: 100%; height: auto; font: 10px sans-serif; user-select: none;");

    const gLink = svg.append("g")
      .attr("fill", "none")
      .attr("stroke", "#555")
      .attr("stroke-opacity", 0.4)
      .attr("stroke-width", 1.5);

    const gNode = svg.append("g")
      .attr("cursor", "pointer")
      .attr("pointer-events", "all");

    function update(event: any, source: any) {
      const duration = event?.altKey ? 2500 : 250;
      const nodes = root.descendants().reverse();
      const links = root.links();

      tree(root);

      let left: any = root;
      let right: any = root;
      root.eachBefore((node: any) => {
        if (node.x < left.x) left = node;
        if (node.x > right.x) right = node;
      });

      const transition: any = svg.transition()
        .duration(duration)
        .attr("height", clientHeight)
        .attr("viewBox", "-100 " + (clientHeight/-2) + " " + clientWidth + " " + clientHeight)

      const node = gNode.selectAll("g")
        .data(nodes, (d: any) => d.id);

      const nodeEnter: any = node.enter().append("g")
        .attr("transform", d => `translate(${source.y0},${source.x0})`)
        .attr("fill-opacity", 0)
        .attr("stroke-opacity", 0)
        .on("click", (event: any, d: any) => {
          d.children = d.children ? null : d._children;
          update(event, d);
        });

      nodeEnter.append("circle")
        .attr("r", 2.5)
        .attr("fill", (d: any) => d._children ? "#555" : "#999")
        .attr("stroke-width", 10);

      nodeEnter.append("text")
        .attr("dy", "0.31em")
        .attr("x", (d: any) => d._children ? -6 : 6)
        .attr("text-anchor", (d: any) => d._children ? "end" : "start")
        .text((d: any) => d.data.name)
        .clone(true).lower()
        .attr("stroke-linejoin", "round")
        .attr("stroke-width", 3)
        .attr("stroke", "white");

      const nodeUpdate = node.merge(nodeEnter).transition(transition)
        .attr("transform", (d: any) => `translate(${d.y},${d.x})`)
        .attr("fill-opacity", 1)
        .attr("stroke-opacity", 1);

      const nodeExit = node.exit().transition(transition).remove()
        .attr("transform", d => `translate(${source.y},${source.x})`)
        .attr("fill-opacity", 0)
        .attr("stroke-opacity", 0);

      const link = gLink.selectAll("path")
        .data(links, (d: any) => d.target.id);

      const linkEnter: any = link.enter().append("path")
        .attr("d", d => {
          const o = { x: source.x0, y: source.y0 };
          return diagonal({ source: o, target: o });
        });

      link.merge(linkEnter).transition(transition)
        .attr("d", diagonal);

      link.exit().transition(transition).remove()
        .attr("d", d => {
          const o = { x: source.x, y: source.y };
          return diagonal({ source: o, target: o });
        });

      root.eachBefore((d: any) => {
        d.x0 = d.x;
        d.y0 = d.y;
      });
    }

    root.x0 = dy / 2;
    root.y0 = 0;
    root.descendants().forEach((d: any, i: any) => {
      d.id = i;
      d._children = d.children;
      if (d.depth == 1) d.children = null;
    });

    update(null, root);
    return svg.node();
  }

  const linkStyle = {
    color: '#0000ff82'
  };

  return (
    <React.Fragment>
      <section className="relative tech-stack p-3" id="hierarchy" >
        <div className="title-container flex flex-col justify-center p-32 gap-6 p-0  w-full items-start py-16">
          <h2>Skills <span className="text-[--orange]">Tree</span></h2>
          <Link to="/#skills" style={linkStyle}>Back</Link>
        </div>
        <div id="treeSkills"></div>
      </section>
    </React.Fragment>
  );
};

export default SkillsTree;
